import { prop } from '../../../../common/utils/immutableHelper';
import { TABLE_GRID_EDITOR_KEY } from '../../../../common/table/tableConstants';
import { Rect } from '../../../../common/maths/geometry/rect/rectTypes';
import { ELEMENT_EDIT_START } from '../../../../common/elements/selectionConstants';
import { UnknownAction } from 'redux';

// Utils
import getParentScrollableSection from '../../../utils/dom/getParentScrollableSection';
import { getSnapPointSheetHeight } from '../../structural/sheet/utils/sheetHeightUtils';
import virtualKeyboardHeightSingleton from '../../../utils/keyboard/virtualKeyboardHeightSingleton';
import {
    getContentDomElement,
    getLabelDomElement,
    getScrollAreaHeightIncreaseOnKeyboardOpen,
    getTargetScrollToAlignElementToCenter,
    ScrollToElementConfig,
} from './scrollToElementUtils';
import { getCurrentCellDOMElement } from '../../../element/table/bridge/utils/domUtils/tableSelectionDomUtils';
import { getDomElementId } from '../../../element/utils/elementUtil';

// Selectors
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import { getSelectedElementIds } from '../../../element/selection/selectedElementsSelector';
import { getSheetState } from '../../structural/sheet/sheetSelectors';
import { getSelectedLabel } from '../../../element/labels/labelSelectors';

// Constants
import {
    DOCUMENT_MODAL_EDITOR_KEY,
    DOCUMENT_MODAL_TITLE_EDITOR_KEY,
} from '../../../element/document/modal/documentModalConstants';
import {
    SheetActionTypes,
    SheetCloseAction,
    SheetUpdateActiveSnapPointAction,
} from '../../structural/sheet/sheetActionTypes';
import { MOBILE_TOOLBAR_HEIGHT, ToolbarLevel } from '../../toolbar/MobileToolbarConstants';
import { CANVAS_VIEWPORT_ID } from '../../../canvas/utils/canvasConstants';
import { POPUP_PANEL_CLASS_NAME } from '../../../components/popupPanel/PopupPanel';
import { LABEL_RENAME_START } from '../../../../common/labels/labelConstants';
import type { ReduxState } from '../../../types/reduxTypes';

type ScrollConfigForDomElementOptions = {
    editorFocusRect?: Rect | undefined;
    keyboardOpenHeightIncrease?: number;
};

/**
 * Determines whether editing this element should scroll it into view.
 */
const shouldScrollOnEditStart = (state: ReduxState, action: UnknownAction) => {
    if (action.preventVirtualKeyboard) return false;

    if (action.editorKey === DOCUMENT_MODAL_TITLE_EDITOR_KEY) return false;

    // The document modal manages its own editor scrolling outside of mobile app mode, so don't move the canvas behind it.
    if (action.editorKey === DOCUMENT_MODAL_EDITOR_KEY && !selectIsMobileAppMode(state)) return false;

    if (action.editorKey === TABLE_GRID_EDITOR_KEY) return false;

    return true;
};

/**
 * Builds scroll config for a DOM node inside the canvas scrollable area.
 */
const getScrollConfigForDomElement = (
    state: ReduxState,
    domElement: HTMLElement | null,
    options: ScrollConfigForDomElementOptions = {},
): ScrollToElementConfig | undefined => {
    const scrollableParent = getParentScrollableSection(domElement);
    if (!domElement || !scrollableParent) return;

    const isInPopupPanel = scrollableParent.closest(`.${POPUP_PANEL_CLASS_NAME}`);
    if (isInPopupPanel) return;

    const isMobileAppMode = selectIsMobileAppMode(state);
    const toolbarHeight = isMobileAppMode ? MOBILE_TOOLBAR_HEIGHT[ToolbarLevel.PRIMARY] : 0;
    const bottomObstructionHeight = virtualKeyboardHeightSingleton.getMaxHeight() + toolbarHeight;
    const keyboardOpenHeightIncrease = isMobileAppMode
        ? (options.keyboardOpenHeightIncrease ?? getScrollAreaHeightIncreaseOnKeyboardOpen())
        : 0;

    return {
        scrollableParent,
        scrollTargetRect: domElement.getBoundingClientRect(),
        editorFocusRect: options.editorFocusRect,
        bottomObstructionHeight,
        keyboardOpenHeightIncrease,
    };
};

/**
 * Builds scroll config when the user starts editing an element.
 */
const getScrollConfigForElementEditStart = (
    state: ReduxState,
    action: UnknownAction,
): ScrollToElementConfig | undefined => {
    if (!shouldScrollOnEditStart(state, action)) return;

    const elementId = action.id as string;
    const editorKey = action.editorKey as string;

    return getScrollConfigForDomElement(state, getContentDomElement(elementId, editorKey), {
        editorFocusRect: action.editorFocusRect as Rect | undefined,
        // The document modal owns its scroll area independently of workspace toolbar spacing.
        keyboardOpenHeightIncrease: editorKey === DOCUMENT_MODAL_EDITOR_KEY ? 0 : undefined,
    });
};

/**
 * Builds scroll config when the user starts renaming a label on a canvas element.
 */
const getScrollConfigForLabelRenameStart = (
    state: ReduxState,
    action: UnknownAction,
): ScrollToElementConfig | undefined => {
    const labelId = action.labelId as string;

    const { _id, elementId } = getSelectedLabel(state) || {};
    if (_id !== labelId || !elementId) return;

    return getScrollConfigForDomElement(state, getLabelDomElement(elementId, labelId));
};

/**
 * For a table target, uses its selected cell rather than the whole table.
 */
const getSelectedContentDomElement = (state: ReduxState, instanceId: string | undefined) => {
    const selectedElementId = instanceId || prop(0, getSelectedElementIds(state));
    const targetDom = document.getElementById(getDomElementId(selectedElementId));
    return (targetDom && getCurrentCellDOMElement(targetDom)) || getContentDomElement(selectedElementId);
};

/**
 * Builds scroll config when a sheet changes its active snap point.
 */
const getScrollConfigForSheetUpdateActiveSnapPoint = (
    state: ReduxState,
    action: UnknownAction,
): ScrollToElementConfig | undefined => {
    const { sheetId, instanceId, activeSnapPoint } = action as SheetUpdateActiveSnapPointAction;

    const sheetState = getSheetState(sheetId, instanceId)(state);
    if (!sheetState || sheetState.activeSnapPoint !== 0) return;

    const bottomObstructionHeight = getSnapPointSheetHeight(activeSnapPoint);

    // If the sheet config specifies to scrollToElementOnOpen, we want to scroll to the selected element.
    if (sheetState.config?.scrollToElementOnOpen) {
        const domElement = getSelectedContentDomElement(state, instanceId);
        const scrollableParent = getParentScrollableSection(domElement);
        if (!domElement || !scrollableParent) return;

        return {
            scrollableParent,
            scrollTargetRect: domElement.getBoundingClientRect(),
            bottomObstructionHeight,
        };
    }

    // Otherwise, apply bottom padding so the canvas remains accessible above the opened sheet.
    const scrollableParent = document.getElementById(CANVAS_VIEWPORT_ID);
    if (!scrollableParent) return;

    return {
        scrollableParent,
        bottomObstructionHeight,
    };
};

/**
 * Returns the scroll effect required before an action is dispatched.
 */
export const getScrollConfigForAction = (
    state: ReduxState,
    action: UnknownAction,
): ScrollToElementConfig | undefined => {
    switch (action.type) {
        case ELEMENT_EDIT_START:
            return getScrollConfigForElementEditStart(state, action);
        case LABEL_RENAME_START:
            return getScrollConfigForLabelRenameStart(state, action);
        case SheetActionTypes.SHEET_UPDATE_ACTIVE_SNAP_POINT:
            return getScrollConfigForSheetUpdateActiveSnapPoint(state, action);
    }
};

/**
 * Returns the scrollTop that re-centres the selection above the keyboard once a sheet closes.
 */
export const getScrollTopToCentreSelectionOnSheetClose = (
    state: ReduxState,
    action: UnknownAction,
): number | undefined => {
    const { sheetId, instanceId } = action as SheetCloseAction;
    if (!getSheetState(sheetId, instanceId)(state)?.config?.scrollToElementOnClose) return;

    const scrollConfig = getScrollConfigForDomElement(state, getSelectedContentDomElement(state, instanceId));
    if (!scrollConfig?.scrollTargetRect) return;

    return getTargetScrollToAlignElementToCenter({ ...scrollConfig, scrollTargetRect: scrollConfig.scrollTargetRect })
        .scrollTop;
};
